class AddCompare extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    this.bindEventListeners();

    this.handle = this.dataset.handle || null;
    if (this.handle) {
      if (this.isAdded(this.handle)) {
        this.classList.add('active');
        this.innerHTML =
          '<i class="fa fa-check" style="color: var(--accentPrimary);"></i> ' +
          `${Shopmonkey.translations.compare_active}`;
        this.removeEventListener('click', this.handleClick);
      }
    }
  }

  bindEventListeners() {
    this.addEventListener('click', this.handleClick);
  }

  async handleClick() {
    try {
      const product = await this.fetchProductJson(this.handle);

      this.addProductToCompare({
        handle: this.handle,
        image: product.featured_image || '',
        title: product.title,
        price: product.price || 0,
      });
    } catch (error) {
      console.error('Failed to fetch product JSON:', error);
    }
  }

  async fetchProductJson(handle) {
    const response = await fetch(window.Shopify.routes.root + 'products/' + handle + '.js');

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    const productJson = await response.json();

    return productJson;
  }

  addProductToCompare(product) {
    let compareList = JSON.parse(localStorage.getItem('shopmonkeyCompareList')) || [];

    if (compareList.length >= 3) {
      compareList.shift();
    }

    compareList.push(product);
    localStorage.setItem('shopmonkeyCompareList', JSON.stringify(compareList));

    this.classList.add('active');
    this.innerHTML =
      '<i class="fa fa-check" style="color: var(--accentPrimary);"></i> ' + `${Shopmonkey.translations.compare_active}`;
    this.removeEventListener('click', this.handleClick);

    this.dispatchEvent(
      new CustomEvent('on:compare:add', {
        bubbles: true,
        composed: true,
        detail: product,
      })
    );
  }

  isAdded(handle) {
    const compareList = JSON.parse(localStorage.getItem('shopmonkeyCompareList')) || [];
    return compareList.some((product) => product.handle === handle);
  }
}

customElements.define('add-compare', AddCompare);

/**
 * Compare Popup
 * This element is used to open the compare popup
 * Path: assets/element-compare.js
 * Usage: <compare-popup></compare-popup>
 */
class ComparePopup extends HTMLElement {
  constructor() {
    super();
    this.handleDelete = this.handleDelete.bind(this);
    this.init();
  }

  init(update = false) {
    this.compareList = this.getCompareList();

    if (this.compareList.length > 0) {
      this.querySelector('.compare-pop').classList.add('active');
      this.querySelector('[data-compare-items]').innerHTML = this.compareList.length;

      this.productsWrap = this.querySelector('[data-compare-products]');
      if (update) {
        this.productsWrap.innerHTML = '';
      }
      this.fetchProducts();
    }
  }

  bindEventListeners() {
    this.deleteButtons = this.querySelectorAll('[data-compare-delete]');
    if (this.deleteButtons.length > 0) {
      this.deleteButtons.forEach((button) => {
        button.addEventListener('click', this.handleDelete);
      });
    }
  }

  getCompareList() {
    return JSON.parse(localStorage.getItem('shopmonkeyCompareList')) || [];
  }

  async fetchProducts() {
    const fetchPromises = this.getCompareList().map(async (product) => {
      try {
        const productObject = await fetch(
          window.Shopify.routes.root + 'products/' + product.handle + '?section_id=component-compare-product-small'
        );
        const productHtml = await productObject.text();
        this.productsWrap.insertAdjacentHTML('beforeend', productHtml);
      } catch (error) {
        console.error('Failed to fetch product HTML:', error);
      }
    });

    await Promise.all(fetchPromises);
    this.bindEventListeners();
  }

  handleDelete(event) {
    const handle = event.target.closest('[data-compare-delete]').getAttribute('data-compare-delete');

    let compareList = this.getCompareList();
    compareList = compareList.filter((product) => product.handle !== handle);
    localStorage.setItem('shopmonkeyCompareList', JSON.stringify(compareList));

    // Update the DOM
    const productElement = event.target.closest('.compare-product');
    if (productElement) {
      productElement.remove();
    }

    // Update compare items count
    this.querySelector('[data-compare-items]').innerHTML = compareList.length;

    // Dispatch custom event
    this.dispatchEvent(
      new CustomEvent('on:compare:remove', {
        bubbles: true,
        composed: true,
        detail: { handle },
      })
    );

    if (compareList.length === 0) {
      this.querySelector('.compare-pop').classList.remove('active').classList.remove('open');
    }
  }
}

customElements.define('compare-popup', ComparePopup);

/**
 * Compare Page
 * This element is used to display the compare page
 * Path: assets/element-compare.js
 * Usage: <compare-page></compare-page>
 */
class ComparePage extends HTMLElement {
  constructor() {
    super();

    this.language = this.dataset.language || 'nl';
    this.labels = this.dataset.labels || '{}';
    this.labelsContainer = this.closest('div').querySelector('custom-compare-labels');
  }

  connectedCallback() {
    this.createLabels();
    this.createPage();
  }

  async createPage() {
    this.enableLoading();

    const compareList = this.getCompareList();

    const fetchPromises = compareList.map(async (product) => {
      try {
        const productObject = await fetch(
          window.Shopify.routes.root + 'products/' + product.handle + '?section_id=component-compare-product-big'
        );
        const productHtml = await productObject.text();

        const productElement = document.createElement('div');
        productElement.innerHTML = productHtml;

        const customCompareDataString = productElement.querySelector('.compare__product').dataset.customCompare;
        let customCompareData;
        try {
          customCompareData = JSON.parse(customCompareDataString);
        } catch (jsonError) {
          console.error('Failed to parse custom compare data:', jsonError);
          customCompareData = [];
        }

        // Create sizes dynamically
        const customItems = this.createSizes(productElement, customCompareData);
        productElement.querySelector('.compare__product--custom-items').replaceWith(customItems);

        this.insertAdjacentHTML('beforeend', productElement.innerHTML);
      } catch (error) {
        console.error('Failed to fetch product HTML:', error);
      }
    });

    await Promise.all(fetchPromises);
    this.disableLoading();
  }

  createLabels() {
    const labels = JSON.parse(this.labels)['en'];

    if (this.labels && this.labelsContainer) {
      labels.forEach((value) => {
        const labelElement = document.createElement('div');
        labelElement.classList.add('compare__label');
        labelElement.innerHTML = Shopmonkey.translations[value];
        this.labelsContainer.appendChild(labelElement);
      });
    }
  }

  createSizes(productElement, customCompareData) {
    const sizeOrder = JSON.parse(this.labels)[this.language];
    const customInfoContainer = productElement.querySelector('.compare__product--custom-items');

    const customCompareMap = customCompareData.reduce((map, field) => {
      const [key, value] = field.split(':');
      map[key.trim().toLowerCase()] = value.trim();
      return map;
    }, {});

    sizeOrder.forEach((sizeKey) => {
      const sizeValue = customCompareMap[sizeKey] || Shopmonkey.translations['nvt'];
      const sizeElement = document.createElement('div');
      sizeElement.classList.add('compare__label', `compare__label--${sizeKey}`);
      sizeElement.innerHTML = `${sizeValue}`;
      customInfoContainer.appendChild(sizeElement);
    });

    return customInfoContainer;
  }

  getCompareList() {
    return JSON.parse(localStorage.getItem('shopmonkeyCompareList')) || [];
  }

  enableLoading() {
    // Add your loading logic here
    console.log('Loading...');
  }

  disableLoading() {
    // Add your loading logic here
    console.log('Loading complete.');
  }
}

customElements.define('compare-page', ComparePage);

// EventListener functions
document.addEventListener('on:compare:add', (event) => {
  liveMessage(Shopmonkey.translations.product_added_to_compare, 'success');
  // reinit compare popup
  document.querySelector('compare-popup').init(true);
});
